import type { MouseEvent } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import {
  Button,
  Input,
  Select,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import {
  INVESTMENT_OBJECTIVE_VALUES,
  type InvestmentObjective,
  patchAccount,
  patchDetails,
} from "~/v2/api/rest/account";
import {
  INVESTMENT_OBJECTIVE_LABEL,
  INVESTMENT_OBJECTIVE_OPTIONS,
} from "~/v2/constants/investmentObjective";
import { FUNDING_SOURCE } from "~/v2/pages/dashboard/onboarding/constants";
import {
  getMinMaxFromValue,
  getValueFromMinMax,
  RANGE_OPTIONS,
} from "~/v2/pages/dashboard/optionsOptIn/pages/financialProfile/FinancialInputs";
import { SectionDropdown } from "../../../../components/common/SectionDropdown";
import { formatCash } from "../../funding/common/util";
import { EMPLOYMENT_OPTIONS } from "../../onboarding/steps/employment";
import { INVESTMENT_EXPERIENCE_OPTIONS } from "../../optionsOptIn/pages/investmentExperience/YearsExperienceInputs";
import { RadiosInput } from "../../optionsOptIn/RadiosInput";
import type { IRAOnboardingProps } from "..";
import { SavedInput } from "./SavedInput";

const schema = z.object({
  annual_income_max: z.number({ error: "Required" }),
  annual_income_min: z.number({ error: "Required" }),
  liquid_net_worth_max: z.number({ error: "Required" }),
  liquid_net_worth_min: z.number({ error: "Required" }),
  total_net_worth_max: z.number({ error: "Required" }),
  total_net_worth_min: z.number({ error: "Required" }),
  funding_source: z.string(),
  employment_status: z.string(),
  employer_address: z.string().optional(),
  employer: z.string().optional(),
  investment_experience_with_options: z.string(),
  investment_experience_with_stocks: z.string(),
  investment_objective: z.enum(INVESTMENT_OBJECTIVE_VALUES, {
    error: "Required",
  }),
});

type FinancialProfileSchema = z.infer<typeof schema>;

const formatCashRange = (
  min: number | null | undefined,
  max: number | null | undefined,
) =>
  min == null || max == null ? "—" : `${formatCash(min)} - ${formatCash(max)}`;

export interface FinancialProfileProps {
  tradeAccount: IRAOnboardingProps["tradeAccount"];
  tradeAccountDetails: IRAOnboardingProps["tradeAccountDetails"];
  refetchTradeAccountDetails: () => void;
  refetchTradeAccount: () => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const FinancialProfile = ({
  tradeAccount,
  tradeAccountDetails,
  refetchTradeAccountDetails,
  refetchTradeAccount,
  isEditing,
  setIsEditing,
  isOpen,
  setIsOpen,
}: FinancialProfileProps) => {
  const {
    watch,
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<FinancialProfileSchema>({
    resolver: zodResolver(
      schema.superRefine((data, ctx) => {
        if (data.employment_status === "EMPLOYED") {
          if (!data.employer) {
            ctx.addIssue({
              path: ["employer"],
              code: z.ZodIssueCode.custom,
              message: "Employer is required",
            });
          }
          if (!data.employer_address) {
            ctx.addIssue({
              path: ["employer_address"],
              code: z.ZodIssueCode.custom,
              message: "Employer address is required",
            });
          }
        }
      }),
    ),
    defaultValues: {
      annual_income_max: tradeAccountDetails.annual_income_max ?? undefined,
      annual_income_min: tradeAccountDetails.annual_income_min ?? undefined,
      liquid_net_worth_max:
        tradeAccountDetails.liquid_net_worth_max ?? undefined,
      liquid_net_worth_min:
        tradeAccountDetails.liquid_net_worth_min ?? undefined,
      total_net_worth_max: tradeAccountDetails.total_net_worth_max ?? undefined,
      total_net_worth_min: tradeAccountDetails.total_net_worth_min ?? undefined,
      funding_source: tradeAccountDetails.funding_source?.at(0) ?? "",
      employment_status: tradeAccountDetails.employment_status || "",
      employer_address: tradeAccountDetails.employer_address || "",
      employer: tradeAccountDetails.employer || "",
      investment_experience_with_options:
        tradeAccountDetails.investment_experience_with_options || "",
      investment_experience_with_stocks:
        tradeAccountDetails.investment_experience_with_stocks || "",
      investment_objective:
        (tradeAccount.investment_objective as InvestmentObjective) || undefined,
    },
    mode: "onChange",
  });

  const saveDetailsMutation = useMutationRequest(
    async ({ investment_objective, ...details }: FinancialProfileSchema) => {
      await patchAccount(tradeAccount.id, {
        investment_objective,
      });
      return patchDetails(tradeAccount.id, {
        ...details,
        funding_source: [details.funding_source],
        employer:
          details.employment_status === "EMPLOYED"
            ? details.employer
            : undefined,
        employer_address:
          details.employment_status === "EMPLOYED"
            ? details.employer_address
            : undefined,
      });
    },
    {
      onSuccess: () => {
        refetchTradeAccountDetails();
        refetchTradeAccount();
        setIsEditing(false);
        toastSuccess("Financial profile updated");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to update financial profile: ${errorMessage}`);
      },
    },
  );

  const onToggleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!isOpen) {
      setIsOpen(true);
      return;
    }
    // only close when not editing
    if (isEditing) {
      return;
    }

    setIsOpen(false);
  };

  const onEditClicked = (event: MouseEvent<HTMLButtonElement>) => {
    // When button re-renders as Save button after this click it triggers form submission even with type=button on this Edit button
    event.preventDefault();
    setIsEditing(true);
    setIsOpen(true);
  };

  const onSubmitValid: SubmitHandler<FinancialProfileSchema> = (data) => {
    if (isEditing) {
      saveDetailsMutation.mutate(data);
    }
  };

  const onSubmitInvalid: SubmitErrorHandler<FinancialProfileSchema> = () => {
    toastError("Please fill in all fields");
  };

  const annualIncome = {
    min: watch("annual_income_min") ?? 0,
    max: watch("annual_income_max") ?? 0,
  };

  const liquidNetWorth = {
    min: watch("liquid_net_worth_min") ?? 0,
    max: watch("liquid_net_worth_max") ?? 0,
  };

  const totalNetWorth = {
    min: watch("total_net_worth_min") ?? 0,
    max: watch("total_net_worth_max") ?? 0,
  };

  const fundingSource = watch("funding_source");
  const employmentStatus = watch("employment_status");
  const investmentExperienceWithOptions = watch(
    "investment_experience_with_options",
  );
  const investmentExperienceWithStocks = watch(
    "investment_experience_with_stocks",
  );

  const onChangeRange =
    (key: "annual_income" | "liquid_net_worth" | "total_net_worth") =>
    (rangeKey: string | string[]) => {
      if (Array.isArray(rangeKey)) {
        rangeKey = rangeKey[0];
      }

      const { min, max } = getMinMaxFromValue(rangeKey);

      setValue(`${key}_min`, min);
      setValue(`${key}_max`, max);
    };

  return (
    <form onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}>
      <SectionDropdown
        title="Financial Profile"
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        className="rounded-lg border border-gray-200 bg-white p-4"
        icon={
          !isEditing ? (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="button"
              onClick={onEditClicked}
            >
              <PencilSimpleIcon size={20} className="fill-gray-700" />
            </Button>
          ) : (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="submit"
              disabled={saveDetailsMutation.isPending}
            >
              Save
            </Button>
          )
        }
      >
        {isEditing ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-3">
              <Select
                label="Annual Income"
                options={RANGE_OPTIONS}
                value={getValueFromMinMax(annualIncome)}
                onChange={onChangeRange("annual_income")}
                errorMsg={errors.annual_income_max?.message}
                status={errors.annual_income_max ? "error" : "neutral"}
              />
              <Select
                label="Estimated Net Worth"
                options={RANGE_OPTIONS}
                value={getValueFromMinMax(totalNetWorth)}
                onChange={onChangeRange("total_net_worth")}
                errorMsg={errors.total_net_worth_max?.message}
                status={errors.total_net_worth_max ? "error" : "neutral"}
              />
            </div>
            <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-3">
              <Select
                label="Liquid Assets"
                options={RANGE_OPTIONS}
                value={getValueFromMinMax(liquidNetWorth)}
                onChange={onChangeRange("liquid_net_worth")}
                errorMsg={errors.liquid_net_worth_max?.message}
                status={errors.liquid_net_worth_max ? "error" : "neutral"}
              />
              <Select
                multiple={false}
                label="Account Funding Source"
                options={FUNDING_SOURCE}
                value={fundingSource}
                onChange={(value) => setValue("funding_source", value)}
                errorMsg={errors.funding_source?.message}
                status={errors.funding_source ? "error" : "neutral"}
              />
            </div>
            <Select
              label="Employment Status"
              options={EMPLOYMENT_OPTIONS}
              value={employmentStatus}
              onChange={(value) => setValue("employment_status", value)}
              errorMsg={errors.employment_status?.message}
              status={errors.employment_status ? "error" : "neutral"}
            />
            {employmentStatus === "EMPLOYED" && (
              <>
                <Input
                  label="Employer"
                  errorMsg={errors.employer?.message}
                  status={errors.employer ? "error" : "neutral"}
                  {...register("employer")}
                />
                <Input
                  label="Employer Address"
                  errorMsg={errors.employer_address?.message}
                  status={errors.employer_address ? "error" : "neutral"}
                  {...register("employer_address")}
                />
              </>
            )}
            <Select
              label="How many years of experience do you have with stocks?"
              options={INVESTMENT_EXPERIENCE_OPTIONS}
              value={investmentExperienceWithStocks}
              onChange={(value) =>
                setValue("investment_experience_with_stocks", value)
              }
              errorMsg={errors.investment_experience_with_stocks?.message}
              status={
                errors.investment_experience_with_stocks ? "error" : "neutral"
              }
            />
            <Select
              label="How many years of experience do you have with options?"
              options={INVESTMENT_EXPERIENCE_OPTIONS}
              value={investmentExperienceWithOptions}
              onChange={(value) =>
                setValue("investment_experience_with_options", value)
              }
              errorMsg={errors.investment_experience_with_options?.message}
              status={
                errors.investment_experience_with_options ? "error" : "neutral"
              }
            />
            <RadiosInput
              id="investment_objective"
              label={INVESTMENT_OBJECTIVE_LABEL}
              value={watch("investment_objective")}
              options={INVESTMENT_OBJECTIVE_OPTIONS}
              onSelect={(value) =>
                setValue("investment_objective", value as InvestmentObjective, {
                  shouldValidate: true,
                })
              }
              errorMessage={errors.investment_objective?.message}
              isRequired
            />
          </div>
        ) : (
          <div className="flex flex-col">
            <SavedInput
              label="Annual Income"
              value={formatCashRange(
                tradeAccountDetails.annual_income_min,
                tradeAccountDetails.annual_income_max,
              )}
            />
            <SavedInput
              label="Estimated Net Worth"
              value={formatCashRange(
                tradeAccountDetails.total_net_worth_min,
                tradeAccountDetails.total_net_worth_max,
              )}
            />
            <SavedInput
              label="Liquid Assets"
              value={formatCashRange(
                tradeAccountDetails.liquid_net_worth_min,
                tradeAccountDetails.liquid_net_worth_max,
              )}
            />
            <SavedInput
              label="Account Funding Source"
              value={startCase(
                tradeAccountDetails.funding_source?.join(", ")?.toLowerCase() ||
                  "",
              )}
            />
            <SavedInput
              label="Employment Status"
              value={startCase(
                tradeAccountDetails.employment_status?.toLowerCase() || "",
              )}
            />
            {tradeAccountDetails.employment_status === "EMPLOYED" && (
              <>
                <SavedInput
                  label="Employer"
                  value={tradeAccountDetails.employer}
                />
                <SavedInput
                  label="Employer Address"
                  value={tradeAccountDetails.employer_address}
                />
              </>
            )}
            <SavedInput
              label="Years of Experience with Stocks"
              value={startCase(
                tradeAccountDetails.investment_experience_with_stocks?.toLowerCase() ||
                  "",
              )}
            />
            <SavedInput
              label="Years of Experience with Options"
              value={startCase(
                tradeAccountDetails.investment_experience_with_options?.toLowerCase() ||
                  "",
              )}
            />
            <SavedInput
              label="Investment Objective"
              value={
                INVESTMENT_OBJECTIVE_OPTIONS.find(
                  (opt) => opt.value === tradeAccount.investment_objective,
                )?.label ?? ""
              }
            />
          </div>
        )}
      </SectionDropdown>
    </form>
  );
};
